import { useState, useEffect, useCallback } from "@wordpress/element";
import apiFetch from "@wordpress/api-fetch";
import {
	PREMIUM_FEATURES_MAP,
	PREMIUM_FEATURE_LABELS,
	PREMIUM_FEATURE_KEYS,
} from "@/constant/premium-features";

const DEFAULT_LICENSE_STATE = {
	isValid: false,
	isActivated: false,
	licenseKey: "",
	licenseData: null,
	premiumFeatures: [],
	isLoading: false,
	error: null,
};

const ajaxUrl = window.ajaxurl || "/wp-admin/admin-ajax.php";

// Function to clean up premium features when license is deactivated
const cleanupPremiumFeatures = () => {
	try {
		// Get currently active features
		const activeFeatures = JSON.parse(
			localStorage.getItem("accessibility-widget-active-features") || "[]",
		);

		// Filter out premium features
		const nonPremiumFeatures = activeFeatures.filter(
			(feature) => !PREMIUM_FEATURE_KEYS.includes(feature),
		);

		// Update localStorage with only non-premium features
		localStorage.setItem(
			"accessibility-widget-active-features",
			JSON.stringify(nonPremiumFeatures),
		);

		// Remove premium feature styles from DOM
		PREMIUM_FEATURE_KEYS.forEach((feature) => {
			// Remove style elements for premium features
			const styleElements = [
				`accessibility-widget-style-${feature}`,
				`accessibility-widget-${feature}`,
				`accessibility-widget-style-${feature}-step`,
			];

			styleElements.forEach((id) => {
				const element = document.getElementById(id);
				if (element) {
					element.remove();
				}
			});
		});

		// Disable specific premium features that have cleanup functions
		if (activeFeatures.includes("screenReader")) {
			// Remove screen reader event listeners
			const handleTextSelection = window.handleTextSelection;
			if (handleTextSelection) {
				document.removeEventListener("mouseup", handleTextSelection);
			}
		}

		if (activeFeatures.includes("readingGuide")) {
			// Remove reading guide
			if (window.disableReadingGuide) {
				window.disableReadingGuide();
			}
		}

		if (activeFeatures.includes("readingMask")) {
			// Remove reading mask
			if (window.removeReadingMask) {
				window.removeReadingMask();
			}
		}

		if (activeFeatures.includes("tooltips")) {
			// Disable tooltips
			if (window.disableTooltips) {
				window.disableTooltips();
			}
		}

		if (activeFeatures.includes("highlightHover")) {
			// Disable highlight hover
			if (window.disableHighlightHover) {
				window.disableHighlightHover();
			}
		}

		if (activeFeatures.includes("magnifiImages")) {
			// Disable magnify images
			if (window.disableMagnifiImages) {
				window.disableMagnifiImages();
			}
		}

		if (activeFeatures.includes("pointerTrail")) {
			// Disable pointer trail
			if (window.disablePointerTrail) {
				window.disablePointerTrail();
			}
		}

		// Reset scaling and other transformations
		if (activeFeatures.includes("contentScaling")) {
			// Reset body zoom
			const body = document.body;
			if (body) {
				body.style.transform = "";
				body.style.transformOrigin = "";
			}
		}

		// Trigger update events
		if (typeof window !== "undefined") {
			window.dispatchEvent(new CustomEvent("accessibility-features-changed"));
		}
	} catch (error) {
		console.error("Error cleaning up premium features:", error);
	}
};

export default function useLicense() {
	const [licenseState, setLicenseState] = useState(DEFAULT_LICENSE_STATE);
	const [initialLoadComplete, setInitialLoadComplete] = useState(false);

	const runAjax = async (action, fields) => {
		const formData = new FormData();
		formData.append("action", action);
		Object.entries(fields || {}).forEach(([k, v]) => formData.append(k, v));

		const res = await apiFetch({
			url: ajaxUrl,
			method: "POST",
			body: formData,
		});
		return res.success ? res.data : res;
	};

	const handleValidLicense = (key, data) => ({
		isValid: true,
		isActivated: true,
		licenseKey: key || licenseState.licenseKey,
		licenseData: data,
		premiumFeatures: PREMIUM_FEATURE_LABELS,
		isLoading: false,
		error: null,
	});

	const handleInvalidLicense = (data, fallbackError) => ({
		...licenseState,
		isValid: false,
		isActivated: false,
		premiumFeatures: [],
		licenseData: data,
		isLoading: false,
		error: data.error || fallbackError,
	});

	const checkLicense = useCallback(async (licenseKey) => {
		if (!licenseKey) return false;
		setLicenseState((s) => ({ ...s, isLoading: true, error: null }));

		try {
			const data = await runAjax("accessibly_check_license", {
				license_key: licenseKey,
			});
			if (data.success && data.license === "valid") {
				setLicenseState((s) => ({
					...s,
					...handleValidLicense(licenseKey, data),
				}));
				return true;
			}
			setLicenseState((s) => ({
				...s,
				...handleInvalidLicense(data, "Invalid license"),
			}));
			return false;
		} catch (err) {
			setLicenseState((s) => ({ ...s, isLoading: false, error: err.message }));
			return false;
		}
	}, []);

	const activateLicense = useCallback(async (licenseKey) => {
		if (!licenseKey) {
			setLicenseState((s) => ({ ...s, error: "License key is required" }));
			return false;
		}
		setLicenseState((s) => ({ ...s, isLoading: true, error: null }));

		try {
			const data = await runAjax("accessibly_activate_license", {
				license_key: licenseKey,
			});
			if (data.success && data.license === "valid") {
				setLicenseState(handleValidLicense(licenseKey, data));
				return true;
			}
			setLicenseState((s) => ({
				...s,
				isLoading: false,
				error: data.error || "Activation failed",
			}));
			return false;
		} catch (err) {
			setLicenseState((s) => ({ ...s, isLoading: false, error: err.message }));
			return false;
		}
	}, []);

	const deactivateLicense = useCallback(async () => {
		if (!licenseState.licenseKey) return false;
		setLicenseState((s) => ({ ...s, isLoading: true, error: null }));

		try {
			const data = await runAjax("accessibly_deactivate_license", {
				license_key: licenseState.licenseKey,
			});
			if (data.success || data.license === "deactivated") {
				// Clean up premium features before updating state
				cleanupPremiumFeatures();

				setLicenseState(DEFAULT_LICENSE_STATE);
				return true;
			}
			setLicenseState((s) => ({
				...s,
				isLoading: false,
				error: data.error || "Deactivation failed",
			}));
			return false;
		} catch (err) {
			setLicenseState((s) => ({ ...s, isLoading: false, error: err.message }));
			return false;
		}
	}, [licenseState.licenseKey]);

	const hasFeature = useCallback(
		(feature) =>
			licenseState.isValid && licenseState.premiumFeatures.includes(feature),
		[licenseState.isValid, licenseState.premiumFeatures],
	);

	const canActivateFeature = useCallback(
		(featureKey) => {
			// If it's not a premium feature, always allow
			if (!PREMIUM_FEATURE_KEYS.includes(featureKey)) {
				return true;
			}
			// If it is premium, check license
			return licenseState.isValid && licenseState.isActivated;
		},
		[licenseState.isValid, licenseState.isActivated],
	);

	const loadSavedLicense = useCallback(async () => {
		try {
			const data = await runAjax("accessibly_get_license");
			if (data.license_key) {
				const valid =
					data.license_data?.success && data.license_data?.license === "valid";

				// If license is invalid, clean up premium features
				if (!valid) {
					cleanupPremiumFeatures();
				}

				setLicenseState((s) => ({
					...s,
					licenseKey: data.license_key,
					licenseData: data.license_data,
					isValid: valid,
					isActivated: valid,
					premiumFeatures: valid ? PREMIUM_FEATURE_LABELS : [],
				}));
			} else {
				// No license key found, clean up premium features
				cleanupPremiumFeatures();
			}
		} catch (err) {
			console.error("Failed to load saved license:", err);
			// On error, also clean up premium features to be safe
			cleanupPremiumFeatures();
		} finally {
			setInitialLoadComplete(true);
		}
	}, []);

	useEffect(() => {
		loadSavedLicense();
	}, [loadSavedLicense]);

	return {
		...licenseState,
		activateLicense,
		deactivateLicense,
		checkLicense,
		hasFeature,
		canActivateFeature,
		cleanupPremiumFeatures, // Export for manual use if needed
		PREMIUM_FEATURES: PREMIUM_FEATURES_MAP,
		initialLoadComplete,
	};
}
